import React, { useEffect, useState, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { useForm, Controller } from "react-hook-form";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  Paper,
  Typography,
  Box,
  Button,
  CircularProgress,
  Grid,
  Card,
  CardContent,
  Slider,
  FormControl,
  FormLabel,
  Chip,
  LinearProgress,
  GlobalStyles,
  Select,
  MenuItem,
  InputLabel,
  CardMedia,
  Avatar,
  Slide,
} from "@mui/material";
import {
  CheckCircle,
  Send,
  NavigateNext,
  NavigateBefore,
  GridView,
} from "@mui/icons-material";
import {
  setCurrentVotes,
  setHasVoted,
  setError,
} from "../store/slices/votingSlice";
import { votingService } from "../services/votingService";
import { eventService } from "../services/eventService";
import { songService } from "../services/songService";
import { scheduleService } from "../services/scheduleService";
import { lineupService } from "../services/lineupService";
import { artistImages, eurovisionArtistImages } from "../assets/artistImages";

const VotingPage = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { eventKey } = useParams();
  const eventId = eventKey || "sanremo";
  const isEurovision = eventId === "eurovision";
  const imagesBySongKey =
    eventId === "eurovision" ? eurovisionArtistImages : artistImages;
  const { user } = useSelector((state) => state.auth);
  const [eventConfig, setEventConfig] = useState(null);
  const [songs, setSongs] = useState([]);
  const [loadingSongs, setLoadingSongs] = useState(true);

  const subEvents = React.useMemo(
    () => (Array.isArray(eventConfig?.subEvents) ? eventConfig.subEvents : []),
    [eventConfig],
  );
  const nights = React.useMemo(() => {
    if (isEurovision) {
      return {
        1: {
          id: 1,
          label: "Finale",
          scheduleKey: "finale",
          isCoverNight: false,
          defaultOrder: null,
          order: 1,
        },
      };
    }
    const byId = {};
    subEvents.forEach((se) => {
      const id = Number(se.id);
      if (!Number.isFinite(id)) return;
      byId[id] = {
        id,
        label: se.label || `Serata ${id}`,
        scheduleKey:
          se.scheduleKey ||
          String(se.label || `serata_${id}`)
            .toLowerCase()
            .replace(" ", "_"),
        isCoverNight: Boolean(se.isCoverNight),
        defaultOrder: Array.isArray(se.defaultOrder) ? se.defaultOrder : null,
        order: Number.isFinite(Number(se.order)) ? Number(se.order) : id,
      };
    });
    return byId;
  }, [subEvents, isEurovision]);
  const orderedNights = React.useMemo(() => {
    return Object.values(nights).sort(
      (a, b) => Number(a.order) - Number(b.order),
    );
  }, [nights]);

  const categories = React.useMemo(() => {
    const remote = Array.isArray(eventConfig?.categories)
      ? eventConfig.categories
      : [];
    return remote
      .map((c, index) => ({
        key: String(c.key || c.id || "").trim(),
        id: String(c.key || c.id || "").trim(),
        label: String(c.label || "").trim(),
        max: Number.isFinite(Number(c.max)) ? Number(c.max) : 10,
        order: Number.isFinite(Number(c.order)) ? Number(c.order) : index + 1,
      }))
      .filter((c) => c.key)
      .sort((a, b) => Number(a.order) - Number(b.order));
  }, [eventConfig]);

  // State per la gestione delle serate
  const [activeNight, setActiveNight] = useState(1);
  const [currentNight, setCurrentNight] = useState(() => {
    if (isEurovision) return 1;
    const nightParam = searchParams.get("night");
    return nightParam ? parseInt(nightParam, 10) : 1;
  });
  const fallbackNightId = orderedNights[0]?.id || 1;
  const effectiveNightId = nights[currentNight]
    ? currentNight
    : fallbackNightId;

  useEffect(() => {
    if (isEurovision && currentNight !== 1) {
      setCurrentNight(1);
    }
  }, [isEurovision, currentNight]);
  const currentNightConfig = React.useMemo(() => {
    return (
      nights[effectiveNightId] || {
        id: effectiveNightId,
        label: `Serata ${effectiveNightId}`,
        scheduleKey: `serata_${effectiveNightId}`,
        isCoverNight: false,
        defaultOrder: null,
      }
    );
  }, [nights, effectiveNightId]);

  const nightKey = React.useMemo(() => {
    return (
      currentNightConfig.scheduleKey ||
      currentNightConfig.label.toLowerCase().replace(" ", "_")
    );
  }, [currentNightConfig]);
  const isVotingOpen = React.useMemo(() => {
    const items = Array.isArray(eventConfig?.subEvents)
      ? eventConfig.subEvents
      : [];
    if (isEurovision) {
      const found = items.find((s) => Number(s.id) === 1);
      if (found && typeof found.isVotingOpen === "boolean")
        return found.isVotingOpen;
      return true;
    }
    const found = items.find((s) => Number(s.id) === Number(currentNight));
    if (found && typeof found.isVotingOpen === "boolean")
      return found.isVotingOpen;
    return true;
  }, [eventConfig, currentNight, isEurovision]);

  const [currentSchedule, setCurrentSchedule] = useState(null);
  const [timeOffset, setTimeOffset] = useState(0);
  const [isScheduleLoaded, setIsScheduleLoaded] = useState(false);
  const [lineupOrder, setLineupOrder] = useState(null);

  useEffect(() => {
    if (isEurovision) {
      setCurrentSchedule(null);
      setTimeOffset(0);
      setIsScheduleLoaded(true);
      return;
    }
    setIsScheduleLoaded(false);
    const unsubscribe = scheduleService.subscribeToSchedule(
      effectiveNightId,
      nightKey,
      (schedule) => {
        if (schedule) {
          setCurrentSchedule(schedule);
          setTimeOffset(schedule.timeOffset || 0);
        }
        setIsScheduleLoaded(true);
      },
      eventId,
    );
    return () => unsubscribe();
  }, [nightKey, eventId, effectiveNightId, isEurovision]);

  useEffect(() => {
    if (!isEurovision) {
      const unsubscribe = lineupService.subscribeToLineup(
        effectiveNightId,
        (order) => setLineupOrder(order || []),
        undefined,
        eventId,
      );
      return () => unsubscribe();
    }

    const primaryNightId = 1;
    const fallbackNightId = 3;
    let fallbackUnsub = null;

    const primaryUnsub = lineupService.subscribeToLineup(
      primaryNightId,
      (order) => {
        if (Array.isArray(order) && order.length > 0) {
          if (fallbackUnsub) {
            fallbackUnsub();
            fallbackUnsub = null;
          }
          setLineupOrder(order);
          return;
        }

        if (!fallbackUnsub) {
          fallbackUnsub = lineupService.subscribeToLineup(
            fallbackNightId,
            (fallbackOrder) => setLineupOrder(fallbackOrder || []),
            undefined,
            eventId,
          );
        }
      },
      undefined,
      eventId,
    );

    return () => {
      primaryUnsub();
      if (fallbackUnsub) fallbackUnsub();
    };
  }, [effectiveNightId, eventId, isEurovision]);

  useEffect(() => {
    if (!nights[currentNight]) {
      setCurrentNight(fallbackNightId);
    }
  }, [eventId, fallbackNightId, nights, currentNight]);

  // Calcola se un artista è live in base alla scaletta
  const isArtistLive = (songKey) => {
    if (!currentSchedule || !currentSchedule.scaletta) return false;

    const now = new Date(new Date().getTime() + timeOffset * 60000);
    const song = songs.find((s) => s.key === songKey);

    if (!song) return false;

    const artistInSchedule = currentSchedule.scaletta.find((item) => {
      // Match using the artist field added by processSchedule
      return item.artist === song.artist;
    });

    if (!artistInSchedule) return false;

    const startTime = artistInSchedule.startTime; // Should be Date object
    const endTime = artistInSchedule.endTime; // Should be Date object

    if (!startTime || !endTime) return false;

    return now >= startTime && now <= endTime;
  };

  const filteredSongs = React.useMemo(() => {
    const all = songs.filter((s) => s && s.key);

    if (Array.isArray(lineupOrder) && lineupOrder.length > 0) {
      const byKey = new Map(all.map((s) => [s.key, s]));
      return lineupOrder.map((k) => byKey.get(k)).filter((s) => s && s.key);
    }

    if (currentSchedule && currentSchedule.scaletta) {
      const artistOrder = {};
      currentSchedule.scaletta.forEach((item, index) => {
        if (item.artist) {
          artistOrder[item.artist] = index;
        }
      });

      const sorted = all.sort((a, b) => {
        const indexA = artistOrder[a.artist];
        const indexB = artistOrder[b.artist];
        if (indexA !== undefined && indexB !== undefined) {
          return indexA - indexB;
        }
        if (indexA !== undefined) return -1;
        if (indexB !== undefined) return 1;
        return (a.order || 0) - (b.order || 0);
      });
      return sorted.filter((s) => s && s.key);
    }

    return all.sort((a, b) => (a.order || 0) - (b.order || 0));
  }, [currentSchedule, songs, lineupOrder]);

  const [currentSongIndex, setCurrentSongIndex] = useState(() => {
    const songParam = searchParams.get("song");
    // Se c'è un parametro song, cerca l'indice nella lista filtrata
    if (songParam) {
      let index = -1;
      if (/^\d+$/.test(songParam)) {
        index = filteredSongs.findIndex(
          (s) => s.order === parseInt(songParam, 10),
        );
      } else {
        index = filteredSongs.findIndex((s) => s.key === songParam);
      }
      return index >= 0 ? index : 0;
    }
    return 0;
  });

  // Query per verificare se l'utente ha già votato
  const { data: userVotes, isLoading } = useQuery({
    queryKey: ["userVotes", eventId, user?.uid],
    queryFn: () => votingService.getUserVotes(user?.uid, eventId),
    enabled: !!user?.uid,
  });

  // Sync with event config
  useEffect(() => {
    const unsubscribe = eventService.subscribeToEvent(
      eventId,
      (ev) => {
        setEventConfig(ev || null);
        if (isEurovision) {
          setActiveNight(1);
          if (!searchParams.get("night")) {
            setCurrentNight(1);
          }
          return;
        }
        const remoteActive = Number(ev?.currentSubEventId);
        if (Number.isFinite(remoteActive)) {
          setActiveNight(remoteActive);
          if (!searchParams.get("night")) {
            setCurrentNight(remoteActive);
          }
        }
      },
      (error) => {
        console.warn("Failed to load event config in VotingPage:", error);
        setEventConfig(null);
      },
    );
    return () => unsubscribe();
  }, [searchParams, eventId, isEurovision]);

  useEffect(() => {
    setLoadingSongs(true);
    const unsubscribe = songService.subscribeToSongs(
      eventId,
      (items) => {
        const normalized = (items || []).map((s) => {
          const baseLabel =
            s?.label ||
            `${s?.artist || ""}${s?.artist && s?.title ? " - " : ""}${s?.title || ""}`.trim() ||
            s?.key;
          return {
            ...s,
            label: baseLabel,
            artist: s?.artist || baseLabel,
            title: s?.title || "",
          };
        });
        setSongs(normalized);
        setLoadingSongs(false);
      },
      (e) => {
        console.warn("Failed to load songs in VotingPage:", e);
        setSongs([]);
        setLoadingSongs(false);
      },
    );
    return () => unsubscribe();
  }, [eventId]);

  // Reset index when night changes or filtered songs update
  // MODIFICA: Questo effect ora cerca di mantenere l'artista corrente se possibile
  useEffect(() => {
    // 1. Controlla se c'è un parametro song nell'URL (priorità massima)
    const songParam = searchParams.get("song");
    if (songParam) {
      let index = -1;
      if (/^\d+$/.test(songParam)) {
        const order = parseInt(songParam, 10);
        index = filteredSongs.findIndex((s) => s.order === order);
      } else {
        index = filteredSongs.findIndex((s) => s.key === songParam);
      }
      if (index >= 0) {
        setCurrentSongIndex(index);
        return;
      }
    }

    // 2. Se abbiamo un currentSongIndex valido, cerchiamo di mantenere lo stesso artista
    // Se l'indice corrente è fuori dai limiti della nuova lista, resettiamo a 0
    if (currentSongIndex >= filteredSongs.length) {
      setCurrentSongIndex(0);
    } else {
      // Altrimenti, verifichiamo se l'artista all'indice corrente è cambiato drasticamente
      // Se la lista è stata riordinata, l'indice potrebbe puntare a un artista diverso.
      // Cerchiamo di ritrovare l'artista precedente nella nuova lista.
      if (lastViewedArtistRef.current) {
        const newIndex = filteredSongs.findIndex(
          (s) => s.key === lastViewedArtistRef.current,
        );
        if (newIndex >= 0 && newIndex !== currentSongIndex) {
          setCurrentSongIndex(newIndex);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentNight, filteredSongs, searchParams]); // Rimosso currentSongIndex dalle dipendenze per evitare loop

  // Ref per tracciare l'ultimo artista visualizzato
  const lastViewedArtistRef = useRef(null);
  const initialJumpDoneRef = useRef(false);

  // Aggiorna il ref ogni volta che cambia la canzone corrente
  useEffect(() => {
    if (filteredSongs[currentSongIndex]) {
      lastViewedArtistRef.current = filteredSongs[currentSongIndex].key;
    }
  }, [currentSongIndex, filteredSongs]);

  // Effect per saltare al primo artista non votato all'avvio
  useEffect(() => {
    // Esegui solo se:
    // 1. Abbiamo i voti dell'utente
    // 2. Abbiamo una lista di canzoni filtrata
    // 3. Non abbiamo ancora fatto il salto iniziale
    // 4. Non c'è un parametro song specifico nell'URL
    // 5. La scaletta è stata caricata (o almeno abbiamo tentato)
    if (
      userVotes?.votes &&
      filteredSongs.length > 0 &&
      !initialJumpDoneRef.current &&
      !searchParams.get("song") &&
      isScheduleLoaded
    ) {
      // Trova il primo indice di canzone non completata
      const firstUnvotedIndex = filteredSongs.findIndex((song) => {
        // Verifica se la canzone è completata
        const isCompleted = categories.every((cat) => {
          // Skip cover category if not cover night
          if (cat.key === "canzone_cover" && !currentNightConfig.isCoverNight) {
            return true;
          }

          let voteKey;
          if (cat.key === "canzone") {
            voteKey = `${song.key}_${cat.key}`;
          } else {
            voteKey = `${song.key}_${currentNight}_${cat.key}`;
          }
          const vote = userVotes.votes[voteKey];
          return vote !== undefined && vote !== null && vote >= 1 && vote <= 10;
        });
        return !isCompleted;
      });

      if (firstUnvotedIndex >= 0) {
        // Verifica se l'indice è diverso da quello attuale per evitare aggiornamenti inutili
        // ma forza l'aggiornamento se siamo a 0 (default) e il primo non votato non è 0
        if (firstUnvotedIndex !== currentSongIndex) {
          setCurrentSongIndex(firstUnvotedIndex);
          // Aggiorna anche il ref dell'ultimo artista visualizzato per coerenza
          if (filteredSongs[firstUnvotedIndex]) {
            lastViewedArtistRef.current = filteredSongs[firstUnvotedIndex].key;
          }
        }
      }
      initialJumpDoneRef.current = true;
    } else if (
      (userVotes || isLoading === false) &&
      filteredSongs.length > 0 &&
      !initialJumpDoneRef.current &&
      isScheduleLoaded // Aspetta che la scaletta sia caricata anche qui
    ) {
      // Se abbiamo finito di caricare o abbiamo i voti (anche vuoti), segniamo come fatto
      // per evitare salti improvvisi successivi
      initialJumpDoneRef.current = true;
    }
  }, [
    userVotes,
    filteredSongs,
    currentNight,
    currentNightConfig,
    searchParams,
    isLoading,
    currentSongIndex,
    isScheduleLoaded,
    categories,
  ]);
  const [completedSongs, setCompletedSongs] = useState(new Set());
  const [allVotes, setAllVotes] = useState({});

  // Live voting states
  const [openedSongs, setOpenedSongs] = useState(new Set()); // Primo stato: card aperta
  const [confirmedSongs, setConfirmedSongs] = useState(new Set()); // Secondo stato: voto confermato
  // Stato conferma per canzone corrente
  const [liveVotes, setLiveVotes] = useState({}); // Voti in tempo reale non ancora confermati

  const currentSong =
    filteredSongs[currentSongIndex] || filteredSongs[0] || null;
  const currentSongKey = currentSong?.key;

  const [showStickyHeader, setShowStickyHeader] = useState(false);
  const imageRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        setShowStickyHeader(
          !entry.isIntersecting && entry.boundingClientRect.top < 0,
        );
      },
      { threshold: 0 },
    );

    const currentImageRef = imageRef.current;
    if (currentImageRef) {
      observer.observe(currentImageRef);
    }

    return () => {
      if (currentImageRef) {
        observer.unobserve(currentImageRef);
      }
    };
  }, [currentSong]);

  const { control, setValue, watch, reset } = useForm({
    defaultValues: categories.reduce((acc, category) => {
      acc[category.key] = 5;
      return acc;
    }, {}),
  });

  const categoriesKey = React.useMemo(
    () => categories.map((c) => c.key).join("|"),
    [categories],
  );

  useEffect(() => {
    reset(
      categories.reduce((acc, category) => {
        acc[category.key] = 5;
        return acc;
      }, {}),
    );
  }, [reset, categoriesKey, categories]);

  const watchedValues = watch();

  // Validation: ensure all categories have votes for current song
  const isCurrentSongValid = () => {
    return categories.every((category) => {
      // Skip validation for cover category if not cover night
      if (
        category.key === "canzone_cover" &&
        !currentNightConfig.isCoverNight
      ) {
        return true;
      }

      const value = watchedValues[category.key];
      return value !== undefined && value !== null && value >= 1 && value <= 10;
    });
  };

  const getCurrentSongScore = () => {
    return categories.reduce((total, category) => {
      // Skip calculation for cover category if not cover night
      if (
        category.key === "canzone_cover" &&
        !currentNightConfig.isCoverNight
      ) {
        return total;
      }

      const value = watchedValues[category.key] || 0;
      return total + value;
    }, 0);
  };

  const getTotalProgress = () => {
    const completedInNight = filteredSongs.filter(
      (s) => s && s.key && completedSongs.has(s.key),
    ).length;
    return (completedInNight / filteredSongs.length) * 100;
  };

  // Live voting functions
  const markSongAsOpened = useCallback((songKey) => {
    setOpenedSongs((prev) => new Set([...prev, songKey]));
  }, []);

  const saveLiveVotes = useCallback(() => {
    if (!currentSongKey) return;
    const songVotes = {};
    categories.forEach((category) => {
      // Skip cover category if not cover night
      if (
        category.key === "canzone_cover" &&
        !currentNightConfig.isCoverNight
      ) {
        return;
      }

      let voteKey;
      if (category.key === "canzone") {
        voteKey = `${currentSongKey}_${category.key}`;
      } else {
        voteKey = `${currentSongKey}_${currentNight}_${category.key}`;
      }
      songVotes[voteKey] = watchedValues[category.key];
    });
    setLiveVotes((prev) => ({ ...prev, ...songVotes }));
  }, [
    currentSongKey,
    watchedValues,
    currentNight,
    currentNightConfig.isCoverNight,
    categories,
  ]);

  const confirmCurrentSongVotes = async () => {
    if (!currentSong) return;
    if (!isCurrentSongValid()) return;

    const songVotes = {};
    const songVotesForDB = {};
    categories.forEach((category) => {
      // Skip cover category if not cover night
      if (
        category.key === "canzone_cover" &&
        !currentNightConfig.isCoverNight
      ) {
        return;
      }

      const score = watchedValues[category.key];
      let voteKey;
      if (category.key === "canzone") {
        voteKey = `${currentSong.key}_${category.key}`;
      } else {
        voteKey = `${currentSong.key}_${currentNight}_${category.key}`;
      }
      songVotes[voteKey] = score;
      songVotesForDB[category.key] = score;
    });

    // Aggiorna lo stato locale
    setAllVotes((prev) => ({ ...prev, ...songVotes }));
    setCompletedSongs((prev) => new Set([...prev, currentSong.key]));
    setConfirmedSongs((prev) => new Set([...prev, currentSong.key]));

    // Salva immediatamente nel database usando la nuova struttura
    try {
      await votingService.submitSingleVote(
        user.uid,
        currentSong && currentSong.key,
        songVotesForDB,
        user.username || user.email,
        currentNight,
        eventId,
      );
      console.log(
        "Voto salvato nel database per:",
        currentSong && currentSong.key,
      );
    } catch (error) {
      console.error("Errore nel salvare il voto:", error);
      dispatch(setError("Errore nel salvare il voto: " + error.message));
    }
  };

  const goToNextSong = () => {
    // Salva automaticamente i voti correnti se validi (DISABILITATO per richiesta utente)
    /* if (isCurrentSongValid()) {
      saveCurrentSongVotes();
    } */

    if (currentSongIndex < filteredSongs.length - 1) {
      setCurrentSongIndex((prev) => prev + 1);
      loadSongVotes(filteredSongs[currentSongIndex + 1]);
      markSongAsOpened(filteredSongs[currentSongIndex + 1].key);
    }
  };

  const goToPreviousSong = () => {
    // Salva automaticamente i voti correnti se validi (DISABILITATO per richiesta utente)
    /* if (isCurrentSongValid()) {
      saveCurrentSongVotes();
    } */

    if (currentSongIndex > 0) {
      setCurrentSongIndex((prev) => prev - 1);
      loadSongVotes(filteredSongs[currentSongIndex - 1]);
      markSongAsOpened(filteredSongs[currentSongIndex - 1].key);
    }
  };

  const loadSongVotes = (song) => {
    categories.forEach((category) => {
      let voteKey;
      if (category.key === "canzone") {
        voteKey = `${song.key}_${category.key}`;
      } else {
        voteKey = `${song.key}_${currentNight}_${category.key}`;
      }

      // Prima controlla i voti dal database, poi lo stato locale, poi i voti live
      const savedVote =
        userVotes?.votes?.[voteKey] || allVotes[voteKey] || liveVotes[voteKey];
      // Default different based on max
      setValue(category.key, savedVote || (category.max === 10 ? 6 : 3));
    });
  };

  const switchToSong = (songIndex) => {
    const song = filteredSongs[songIndex];
    if (!song) return;
    setCurrentSongIndex(songIndex);
    loadSongVotes(song);
    markSongAsOpened(song.key);
  };

  useEffect(() => {
    if (userVotes) {
      dispatch(setHasVoted(true));
      dispatch(setCurrentVotes(userVotes.votes));

      // Aggiorna lo stato locale con i voti dal database
      setAllVotes(userVotes.votes);

      // Identifica le canzoni completate
      const completed = new Set();
      songs.forEach((song) => {
        const isCompleted = categories.every((cat) => {
          // Skip cover category if not cover night
          if (cat.key === "canzone_cover" && !currentNightConfig.isCoverNight) {
            return true;
          }

          let voteKey;
          if (cat.key === "canzone") {
            voteKey = `${song.key}_${cat.key}`;
          } else {
            voteKey = `${song.key}_${currentNight}_${cat.key}`;
          }
          const vote = userVotes.votes[voteKey];
          return vote !== undefined && vote !== null && vote >= 1 && vote <= 10;
        });
        if (isCompleted) {
          completed.add(song.key);
        }
      });
      setCompletedSongs(completed);
      setConfirmedSongs(completed);
    }
  }, [
    userVotes,
    dispatch,
    setValue,
    currentNight,
    currentNightConfig.isCoverNight,
    songs,
    categories,
  ]);

  // Carica i voti per la canzone corrente quando cambia
  useEffect(() => {
    if (userVotes?.votes && currentSongKey) {
      categories.forEach((category) => {
        let voteKey;
        if (category.key === "canzone") {
          voteKey = `${currentSongKey}_${category.key}`;
        } else {
          voteKey = `${currentSongKey}_${currentNight}_${category.key}`;
        }
        const savedVote = userVotes.votes[voteKey];
        setValue(category.key, savedVote || (category.max === 10 ? 6 : 3));
      });
    }
  }, [userVotes, setValue, currentSongKey, currentNight, categories]);

  // Live voting effect - salva automaticamente i voti mentre l'utente li modifica
  useEffect(() => {
    if (!currentSongKey) return;
    if (openedSongs.has(currentSongKey)) {
      const timer = setTimeout(() => {
        saveLiveVotes();
      }, 500); // Debounce di 500ms

      return () => clearTimeout(timer);
    }
  }, [watchedValues, currentSongKey, openedSongs, saveLiveVotes]);

  // Marca la prima canzone come aperta all'avvio
  useEffect(() => {
    if (!currentSongKey) return;
    markSongAsOpened(currentSongKey);
  }, [currentSongKey, markSongAsOpened]);

  const handleGoToSummary = () => {
    // Navigate to summary without finalizing
    navigate(`/${eventId}/vote-success?night=${currentNight}&partial=true`);
  };

  if (isLoading || loadingSongs) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (filteredSongs.length === 0) {
    return (
      <Box
        display="flex"
        flexDirection="column"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
        gap={2}
      >
        <Typography variant="h6">Nessuna canzone disponibile</Typography>
        <Typography variant="body2" color="text.secondary">
          Importa i segmenti dell'evento in Firestore da Admin.
        </Typography>
        <Button variant="contained" onClick={() => navigate(`/${eventId}`)}>
          Torna all'evento
        </Button>
      </Box>
    );
  }

  if (!currentSong) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <GlobalStyles
        styles={{
          "@keyframes pulse": {
            "0%": {
              opacity: 1,
            },
            "50%": {
              opacity: 0.5,
            },
            "100%": {
              opacity: 1,
            },
          },
        }}
      />
      {currentSong && (
        <Slide
          direction="down"
          in={showStickyHeader}
          mountOnEnter
          unmountOnExit
        >
          <Paper
            elevation={4}
            sx={{
              position: "fixed",
              top: 0,
              left: 0,
              right: 0,
              zIndex: 1100,
              p: 1,
              bgcolor: "background.paper",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 0,
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              gap={2}
              maxWidth="lg"
              width="100%"
              px={2}
            >
              <Avatar
                src={
                  currentSong.imageUrl ||
                  imagesBySongKey[currentSong.key] ||
                  currentSong.image ||
                  "/placeholder.jpg"
                }
                alt={currentSong.artist}
                sx={{ width: 40, height: 40 }}
              />
              <Box overflow="hidden">
                <Typography variant="subtitle1" fontWeight="bold" noWrap>
                  {currentSong.artist}
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  noWrap
                  display="block"
                >
                  {currentSong.title}
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Slide>
      )}
      <Paper elevation={3} sx={{ p: 4, maxWidth: 1200, mx: "auto", mt: 4 }}>
        {/* Progress Bar */}
        <Box mb={4}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            mb={1}
          >
            <Typography variant="body2" color="text.secondary">
              Progresso: {completedSongs.size}/{filteredSongs.length} canzoni
              completate
            </Typography>
            <Typography variant="body2" color="primary">
              {Math.round(getTotalProgress())}%
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={getTotalProgress()}
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>

        {currentSong ? (
          <>
            {/* Current Song Card */}
            <Card
              elevation={2}
              sx={{ mb: 4, border: "2px solid", borderColor: "primary.main" }}
            >
              <CardContent sx={{ p: 4 }}>
                <Box
                  display="flex"
                  flexDirection={{ xs: "column", md: "row" }}
                  alignItems={{ xs: "center", md: "flex-start" }}
                  mb={3}
                  gap={4}
                >
                  <CardMedia
                    ref={imageRef}
                    component="img"
                    image={
                      currentSong.imageUrl ||
                      imagesBySongKey[currentSong.key] ||
                      currentSong.image ||
                      "/placeholder.jpg"
                    }
                    alt={currentSong.artist}
                    sx={{
                      width: { xs: "100%", sm: 300, md: 250 },
                      maxWidth: { xs: 300, md: "none" },
                      height: { xs: 300, sm: 300, md: 250 },
                      borderRadius: 2,
                      objectFit: "cover",
                      boxShadow: 3,
                    }}
                  />
                  <Box
                    flex={1}
                    textAlign={{ xs: "center", md: "left" }}
                    width="100%"
                  >
                    {currentNightConfig.isCoverNight ? (
                      <>
                        <Typography variant="h4" gutterBottom>
                          {currentSong.artist}
                        </Typography>
                        <Typography variant="h6" color="primary.main">
                          COVER: "{currentSong.cover?.title}"
                        </Typography>
                        {currentSong.cover?.guest && (
                          <Typography
                            variant="subtitle1"
                            color="text.secondary"
                          >
                            con {currentSong.cover.guest}
                          </Typography>
                        )}
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ fontStyle: "italic" }}
                        >
                          (Originale: {currentSong.cover?.original})
                        </Typography>
                      </>
                    ) : (
                      <>
                        <Typography variant="h4" gutterBottom>
                          {currentSong.artist}
                        </Typography>
                        <Typography variant="h5" color="text.secondary">
                          "{currentSong.title}"
                        </Typography>
                      </>
                    )}

                    {/* Live voting status indicators */}
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent={{ xs: "center", md: "flex-start" }}
                      gap={1}
                      mt={2}
                    >
                      {confirmedSongs.has(currentSong.key) && (
                        <Chip
                          label="Voto Confermato"
                          size="small"
                          color="success"
                          icon={<CheckCircle />}
                        />
                      )}
                      {openedSongs.has(currentSong.key) &&
                        !confirmedSongs.has(currentSong.key) && (
                          <Chip
                            label="Votazione in corso..."
                            size="small"
                            color="warning"
                            variant="outlined"
                          />
                        )}
                    </Box>
                  </Box>
                  {completedSongs.has(currentSong.key) && (
                    <CheckCircle
                      color="success"
                      sx={{ ml: "auto", fontSize: 30 }}
                    />
                  )}
                </Box>

                <form>
                  {isEurovision ? (
                    <Box mb={3}>
                      <Typography
                        variant="h6"
                        gutterBottom
                        sx={{ borderBottom: 1, borderColor: "divider", pb: 1 }}
                      >
                        Voti Finale
                      </Typography>
                      <Grid container spacing={3}>
                        {categories
                          .filter((c) => {
                            if (c.key === "canzone_cover") {
                              return currentNightConfig.isCoverNight;
                            }
                            return true;
                          })
                          .map((category) => {
                            const voteKey =
                              category.key === "canzone"
                                ? `${currentSong.key}_${category.key}`
                                : `${currentSong.key}_${currentNight}_${category.key}`;
                            const savedVote = userVotes?.votes?.[voteKey];
                            const isVotingDisabled = !isVotingOpen;

                            return (
                              <Grid item xs={12} md={6} key={category.key}>
                                <Card
                                  variant="outlined"
                                  sx={{
                                    p: 2,
                                    bgcolor: isVotingDisabled
                                      ? "action.disabledBackground"
                                      : "grey.50",
                                    opacity: isVotingDisabled ? 0.7 : 1,
                                  }}
                                >
                                  <FormControl
                                    fullWidth
                                    disabled={isVotingDisabled}
                                  >
                                    <FormLabel
                                      sx={{
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                      }}
                                    >
                                      <Box>
                                        <Typography
                                          variant="subtitle1"
                                          sx={{
                                            color: isVotingDisabled
                                              ? "text.disabled"
                                              : category.color,
                                            fontWeight: "bold",
                                          }}
                                        >
                                          {category.label}{" "}
                                          {isVotingDisabled &&
                                            "(Votazione Chiusa)"}
                                        </Typography>
                                        <Typography
                                          variant="body2"
                                          color="text.secondary"
                                        >
                                          {category.description}
                                        </Typography>
                                      </Box>
                                      {savedVote && (
                                        <Chip
                                          label={`Voto salvato: ${savedVote}`}
                                          size="small"
                                          color="primary"
                                          variant="outlined"
                                        />
                                      )}
                                    </FormLabel>

                                    <Box sx={{ px: 2, mt: 2 }}>
                                      <Controller
                                        name={category.key}
                                        control={control}
                                        render={({ field }) => (
                                          <Box>
                                            <Slider
                                              {...field}
                                              min={1}
                                              max={category.max}
                                              step={1}
                                              marks
                                              valueLabelDisplay="on"
                                              sx={{
                                                color: category.color,
                                                "& .MuiSlider-thumb": {
                                                  bgcolor: category.color,
                                                },
                                                "& .MuiSlider-track": {
                                                  bgcolor: category.color,
                                                },
                                                "& .MuiSlider-rail": {
                                                  bgcolor: "grey.300",
                                                },
                                              }}
                                            />
                                            <Box
                                              display="flex"
                                              justifyContent="space-between"
                                              mt={1}
                                            >
                                              <Typography variant="caption">
                                                1
                                              </Typography>
                                              <Typography
                                                variant="h6"
                                                sx={{
                                                  color: category.color,
                                                  fontWeight: "bold",
                                                }}
                                              >
                                                {field.value}/{category.max}
                                              </Typography>
                                              <Typography variant="caption">
                                                {category.max}
                                              </Typography>
                                            </Box>
                                          </Box>
                                        )}
                                      />
                                    </Box>
                                  </FormControl>
                                </Card>
                              </Grid>
                            );
                          })}
                      </Grid>
                    </Box>
                  ) : (
                    <>
                      <Box mb={4}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            pb: 1,
                          }}
                        >
                          Voto Generale (Valido per tutte le serate)
                        </Typography>
                        <Grid container spacing={3}>
                          {categories
                            .filter((c) => c.key === "canzone")
                            .map((category) => {
                              const savedVote =
                                userVotes?.votes?.[
                                  `${currentSong.key}_${category.key}`
                                ];
                              return (
                                <Grid item xs={12} key={category.key}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      bgcolor: "grey.50",
                                      borderColor: category.color,
                                      borderWidth: 2,
                                    }}
                                  >
                                    <FormControl fullWidth>
                                      <FormLabel
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box>
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: category.color,
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {category.label}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {category.description}
                                          </Typography>
                                        </Box>
                                        {savedVote && (
                                          <Chip
                                            label={`Voto salvato: ${savedVote}`}
                                            size="small"
                                            color="success"
                                            variant="outlined"
                                          />
                                        )}
                                      </FormLabel>

                                      <Box sx={{ px: 2, mt: 2 }}>
                                        <Controller
                                          name={category.key}
                                          control={control}
                                          render={({ field }) => (
                                            <Box>
                                              <Slider
                                                {...field}
                                                min={1}
                                                max={category.max}
                                                step={1}
                                                marks
                                                valueLabelDisplay="on"
                                                sx={{
                                                  color: category.color,
                                                  "& .MuiSlider-thumb": {
                                                    bgcolor: category.color,
                                                  },
                                                  "& .MuiSlider-track": {
                                                    bgcolor: category.color,
                                                  },
                                                  "& .MuiSlider-rail": {
                                                    bgcolor: "grey.300",
                                                  },
                                                }}
                                              />
                                              <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mt={1}
                                              >
                                                <Typography variant="caption">
                                                  1
                                                </Typography>
                                                <Typography
                                                  variant="h6"
                                                  sx={{
                                                    color: category.color,
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  {field.value}/{category.max}
                                                </Typography>
                                                <Typography variant="caption">
                                                  {category.max}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}
                                        />
                                      </Box>
                                    </FormControl>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Box>

                      <Box mb={3}>
                        <Typography
                          variant="h6"
                          gutterBottom
                          sx={{
                            borderBottom: 1,
                            borderColor: "divider",
                            pb: 1,
                          }}
                        >
                          Voti Serata {currentNight} (Specifici per questa
                          esibizione)
                        </Typography>
                        <Grid container spacing={3}>
                          {categories
                            .filter((c) => {
                              if (c.key === "canzone") return false;
                              if (c.key === "canzone_cover") {
                                return currentNightConfig.isCoverNight;
                              }
                              return true;
                            })
                            .map((category) => {
                              const savedVote =
                                userVotes?.votes?.[
                                  `${currentSong.key}_${currentNight}_${category.key}`
                                ];
                              const isVotingDisabled =
                                (!isEurovision &&
                                  currentNight !== activeNight) ||
                                !isVotingOpen;

                              return (
                                <Grid item xs={12} md={6} key={category.key}>
                                  <Card
                                    variant="outlined"
                                    sx={{
                                      p: 2,
                                      bgcolor: isVotingDisabled
                                        ? "action.disabledBackground"
                                        : "grey.50",
                                      opacity: isVotingDisabled ? 0.7 : 1,
                                    }}
                                  >
                                    <FormControl
                                      fullWidth
                                      disabled={isVotingDisabled}
                                    >
                                      <FormLabel
                                        sx={{
                                          display: "flex",
                                          justifyContent: "space-between",
                                          alignItems: "center",
                                        }}
                                      >
                                        <Box>
                                          <Typography
                                            variant="subtitle1"
                                            sx={{
                                              color: isVotingDisabled
                                                ? "text.disabled"
                                                : category.color,
                                              fontWeight: "bold",
                                            }}
                                          >
                                            {category.label}{" "}
                                            {isVotingDisabled &&
                                              "(Votazione Chiusa)"}
                                          </Typography>
                                          <Typography
                                            variant="body2"
                                            color="text.secondary"
                                          >
                                            {category.description}
                                          </Typography>
                                        </Box>
                                        {savedVote && (
                                          <Chip
                                            label={`Voto salvato: ${savedVote}`}
                                            size="small"
                                            color="primary"
                                            variant="outlined"
                                          />
                                        )}
                                      </FormLabel>

                                      <Box sx={{ px: 2, mt: 2 }}>
                                        <Controller
                                          name={category.key}
                                          control={control}
                                          render={({ field }) => (
                                            <Box>
                                              <Slider
                                                {...field}
                                                min={1}
                                                max={category.max}
                                                step={1}
                                                marks
                                                valueLabelDisplay="on"
                                                sx={{
                                                  color: category.color,
                                                  "& .MuiSlider-thumb": {
                                                    bgcolor: category.color,
                                                  },
                                                  "& .MuiSlider-track": {
                                                    bgcolor: category.color,
                                                  },
                                                  "& .MuiSlider-rail": {
                                                    bgcolor: "grey.300",
                                                  },
                                                }}
                                              />
                                              <Box
                                                display="flex"
                                                justifyContent="space-between"
                                                mt={1}
                                              >
                                                <Typography variant="caption">
                                                  1
                                                </Typography>
                                                <Typography
                                                  variant="h6"
                                                  sx={{
                                                    color: category.color,
                                                    fontWeight: "bold",
                                                  }}
                                                >
                                                  {field.value}/{category.max}
                                                </Typography>
                                                <Typography variant="caption">
                                                  {category.max}
                                                </Typography>
                                              </Box>
                                            </Box>
                                          )}
                                        />
                                      </Box>
                                    </FormControl>
                                  </Card>
                                </Grid>
                              );
                            })}
                        </Grid>
                      </Box>
                    </>
                  )}

                  <Box mt={3} p={2} bgcolor="primary.50" borderRadius={2}>
                    <Typography variant="h6" textAlign="center" color="primary">
                      Punteggio Totale: {getCurrentSongScore()}
                    </Typography>

                    {/* Confirm vote button */}
                    {openedSongs.has(currentSong.key) &&
                      isCurrentSongValid() && (
                        <Box textAlign="center" mt={2}>
                          <Button
                            variant="contained"
                            color={
                              confirmedSongs.has(currentSong.key)
                                ? "primary"
                                : "success"
                            }
                            onClick={confirmCurrentSongVotes}
                            startIcon={<CheckCircle />}
                            size="large"
                          >
                            {confirmedSongs.has(currentSong.key)
                              ? "Aggiorna Voto"
                              : "Conferma Voto per questa Canzone"}
                          </Button>
                        </Box>
                      )}

                    {openedSongs.has(currentSong.key) &&
                      !isCurrentSongValid() && (
                        <Box textAlign="center" mt={2}>
                          <Typography variant="body2" color="warning.main">
                            Completa tutte le categorie per confermare il voto
                          </Typography>
                        </Box>
                      )}
                  </Box>
                </form>
              </CardContent>
            </Card>

            {/* Navigation */}
            <Box
              mt={4}
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              mb={8}
            >
              <Button
                variant="outlined"
                onClick={goToPreviousSong}
                disabled={currentSongIndex === 0}
                size="large"
                sx={{ minWidth: 0, p: 2, borderRadius: "50%" }}
              >
                <NavigateBefore fontSize="large" />
              </Button>

              <Box textAlign="center">
                <Typography variant="body1" color="text.secondary">
                  {currentSongIndex + 1} di {filteredSongs.length}
                </Typography>
                {!isCurrentSongValid() && (
                  <Typography variant="body2" color="warning.main">
                    Completa tutte le categorie per salvare automaticamente
                  </Typography>
                )}
              </Box>

              {currentSongIndex < filteredSongs.length - 1 && (
                <Button
                  variant="contained"
                  onClick={goToNextSong}
                  size="large"
                  sx={{ minWidth: 0, p: 2, borderRadius: "50%" }}
                >
                  <NavigateNext fontSize="large" />
                </Button>
              )}
            </Box>
          </>
        ) : (
          <Box textAlign="center" my={10}>
            <Typography variant="h5">
              Nessuna canzone disponibile per questa serata.
            </Typography>
          </Box>
        )}

        {/* Songs Overview Dropdown */}
        <Card variant="outlined" sx={{ mb: 4, mt: 4 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Vai alla canzone ({currentNightConfig.label})
            </Typography>
            <FormControl fullWidth>
              <InputLabel id="song-select-label">Seleziona Canzone</InputLabel>
              <Select
                labelId="song-select-label"
                value={currentSongIndex}
                label="Seleziona Canzone"
                onChange={(e) => switchToSong(e.target.value)}
              >
                {filteredSongs.filter(Boolean).map((song, index) => (
                  <MenuItem key={song.key} value={index}>
                    <Box
                      display="flex"
                      alignItems="center"
                      justifyContent="space-between"
                      width="100%"
                    >
                      <Box display="flex" alignItems="center" gap={1}>
                        <Typography variant="body2" fontWeight="bold">
                          #{song.order}
                        </Typography>
                        <Typography variant="body2">
                          {song.artist} - {song.title}
                        </Typography>
                      </Box>
                      <Box display="flex" alignItems="center" gap={1}>
                        {isArtistLive(song.key) && (
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: "50%",
                              bgcolor: "error.main",
                              animation: "pulse 1s infinite",
                            }}
                            title="Live ora!"
                          />
                        )}
                        {confirmedSongs.has(song.key) && (
                          <CheckCircle color="success" sx={{ fontSize: 16 }} />
                        )}
                      </Box>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </CardContent>
        </Card>

        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Box
            display="flex"
            flexDirection={{ xs: "column", md: "row" }}
            alignItems="center"
            justifyContent="center"
            gap={2}
            sx={{ maxWidth: 600, mx: "auto" }}
          >
            {!isEurovision && (
              <FormControl fullWidth sx={{ maxWidth: 300 }}>
                <InputLabel id="night-select-label">
                  Seleziona Serata
                </InputLabel>
                <Select
                  labelId="night-select-label"
                  value={currentNight}
                  label="Seleziona Serata"
                  onChange={(e) => setCurrentNight(Number(e.target.value))}
                >
                  {Object.values(nights).map((night) => (
                    <MenuItem key={night.id} value={night.id}>
                      {night.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            )}

            <Button
              variant="outlined"
              startIcon={<GridView />}
              onClick={() => navigate(`/${eventId}/artists`)}
              sx={{ height: 56, minWidth: 200 }}
            >
              Griglia Artisti
            </Button>
            <Button
              variant="contained"
              color="success"
              endIcon={<Send />}
              onClick={handleGoToSummary}
              size="large"
              sx={{ height: 56, minWidth: 200 }}
            >
              Vedi Riepilogo
            </Button>
          </Box>
        </Box>
      </Paper>
    </>
  );
};

export default VotingPage;
